import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/rameon-logo.png";
import { products } from "@/data/products";
import ZLDCalculator from "@/components/ZLDCalculator";
import { ThemeToggle } from "@/components/ThemeToggle";
interface NavLink {
  name: string;
  href: string;
  isRoute?: boolean;
  hasMegaMenu?: boolean;
}

const navLinks: NavLink[] = [
  { name: "Home", href: "/" , isRoute: true },
  { name: "About Us", href: "/#about" },
  { name: "Solutions", href: "/solutions", isRoute: true },
  { name: "Products", href: "/products", isRoute: true, hasMegaMenu: true },
  { name: "Services", href: "/services", isRoute: true },
  { name: "Clients", href: "/clients", isRoute: true },
  { name: "Insights", href: "/blog", isRoute: true },
  { name: "Contact", href: "/#contact" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const backdropTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;
    let lastKnownScrollY = 0;
    
    const updateScrollState = () => {
      if (isMobileMenuOpen) {
        ticking = false;
        return;
      }
      
      const currentScrollY = lastKnownScrollY;
      const prevScrollY = lastScrollYRef.current;
      
      setIsScrolled(currentScrollY > 100);
      
      // Only trigger hide/show with a minimum scroll delta to prevent flicker
      const scrollDelta = currentScrollY - prevScrollY;
      
      if (scrollDelta > 5 && currentScrollY > 150) {
        // Scrolling down past threshold — hide
        setIsHidden(true);
      } else if (scrollDelta < -5) {
        // Scrolling up — show
        setIsHidden(false);
      }
      
      lastScrollYRef.current = currentScrollY;
      ticking = false;
    };
    
    const handleScroll = () => {
      if (isMobileMenuOpen) return;
      
      lastKnownScrollY = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMobileMenuOpen) {
      handleCloseMenu();
    }
  }, [location.pathname]);

  // Prevent body scroll using overflow-based lock (no position change = no jump)
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Calculate scrollbar width for compensation
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      document.body.classList.add('mobile-menu-open');
      
      // Prevent touchmove on document to stop iOS Safari rubber-banding
      const preventTouchMove = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('#mobile-menu')) {
          return;
        }
        e.preventDefault();
      };
      
      document.addEventListener('touchmove', preventTouchMove, { passive: false });
      
      return () => {
        document.removeEventListener('touchmove', preventTouchMove);
        document.body.classList.remove('mobile-menu-open');
        document.documentElement.style.removeProperty('--scrollbar-width');
      };
    }
  }, [isMobileMenuOpen]);

  // Lock page scroll while the desktop Products mega menu is open
  useEffect(() => {
    if (!isProductsMenuOpen) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, [isProductsMenuOpen]);

  const handleCloseMenu = () => {
    backdropTouchStartRef.current = null;
    setIsMobileMenuOpen(false);
    setIsMobileProductsOpen(false);
  };

  // Handle backdrop tap - only close on deliberate taps, not scrolls
  const handleBackdropTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    backdropTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleBackdropTouchEnd = (e: React.TouchEvent) => {
    const start = backdropTouchStartRef.current;
    if (!start) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - start.x);
    const deltaY = Math.abs(touch.clientY - start.y);

    if (deltaX < 10 && deltaY < 10) {
      handleCloseMenu();
    }

    backdropTouchStartRef.current = null;
  };

  const handleNavClick = (link: NavLink) => {
    handleCloseMenu();
    
    if (link.isRoute) {
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (link.href.includes("#")) {
      const [path, hash] = link.href.split("#");
      if (location.pathname === "/" || location.pathname === path) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        navigate(link.href);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 300);
      }
    }
  };

  // Determine header styles based on scroll and page
  const headerBg = isScrolled
    ? "glass-nav"
    : "bg-transparent";

  
  const textColor = "text-primary";

  return (
    <>
      <header
        id="site-header"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ease-apple ${headerBg} ${
          isScrolled ? "py-3" : "py-6"
        } ${isHidden && !isMobileMenuOpen ? "-translate-y-full" : "translate-y-0"}`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 relative z-10" aria-label="Rameon Energy - Go to homepage">
            <img
              src={logo}
              alt="Rameon Energy"
              width={180}
              height={60}
              loading="eager"
              decoding="async"
              className={`transition-all duration-500 ${
                isScrolled ? "w-[120px] md:w-[140px] lg:w-[150px]" : "w-[140px] md:w-[180px] lg:w-[200px] xl:w-[220px]"
              } h-auto`}
            />
          </Link>

          {/* Vertical Divider - Desktop only */}
          <div 
            className={`hidden lg:block h-6 w-px transition-all duration-500 ml-6 xl:ml-8 ${
              isScrolled 
                ? 'bg-border dark:bg-border' 
                : 'bg-white/30'
            }`}
            aria-hidden="true"
          />

          {/* Desktop Navigation - Centered with breathing room from logo */}
          <nav className="hidden lg:flex items-center justify-center absolute left-1/2 -translate-x-1/2 ml-12 xl:ml-16 2xl:ml-20 3xl:ml-24">
            <div className="flex items-center gap-5 lg:gap-6 xl:gap-7 3xl:gap-8 5xl:gap-10">
              {navLinks.map((link) => (
                link.hasMegaMenu ? (
                  <div
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setIsProductsMenuOpen(true)}
                    onMouseLeave={() => setIsProductsMenuOpen(false)}
                  >
                    <button
                      onClick={() => handleNavClick(link)}
                      aria-expanded={isProductsMenuOpen}
                      aria-haspopup="menu"
                      aria-label="Products navigation menu - Browse ZLD equipment and evaporators"
                      className={`font-poppins text-sm lg:text-base 3xl:text-lg font-medium transition-colors duration-300 ${textColor} hover:text-secondary whitespace-nowrap flex items-center gap-1 border-0 bg-transparent outline-none`}
                    >
                      {link.name}
                      <ChevronDown 
                        size={16} 
                        aria-hidden="true"
                        className={`transition-transform duration-300 ${isProductsMenuOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    
                    {/* Mega Menu Dropdown */}
                    <div 
                      className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300 ${
                        isProductsMenuOpen 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      <div className="bg-background border border-border/60 shadow-2xl rounded-2xl p-5 3xl:p-6 min-w-[560px] 3xl:min-w-[640px] 5xl:min-w-[700px]">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/30">
                          <h3 className="text-sm font-semibold text-foreground/60 uppercase tracking-wider">Our Products</h3>
                          <button
                            onClick={() => {
                              setIsProductsMenuOpen(false);
                              handleNavClick(link);
                            }}
                            aria-label="View all products"
                            className="text-xs font-medium text-primary hover:text-secondary transition-colors"
                          >
                            View All →
                          </button>
                        </div>
                        
                        {/* Products Grid */}
                        <div className="grid grid-cols-2 gap-2">
                          {products.map((product) => (
                            <Link
                              key={product.id}
                              to={`/products/${product.id}`}
                              onClick={() => setIsProductsMenuOpen(false)}
                              className="group flex items-center gap-3 p-3 rounded-xl hover:bg-primary/5 transition-all duration-200"
                            >
                              <div className="w-12 h-12 rounded-lg bg-muted/50 overflow-hidden flex-shrink-0 group-hover:ring-2 ring-primary/20 transition-all">
                                <img 
                                  src={product.image} 
                                  alt={product.shortName}
                                  width={48}
                                  height={48}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                                  {product.shortName}
                                </h4>
                                <p className="text-xs text-foreground/50 truncate">
                                  {product.tagline.slice(0, 40)}...
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => handleNavClick(link)}
                    aria-label={`Navigate to ${link.name} ${link.isRoute ? 'page' : 'section'}`}
                    className={`font-poppins text-sm lg:text-base 3xl:text-lg font-medium transition-colors duration-300 link-underline ${textColor} hover:text-secondary whitespace-nowrap`}
                  >
                    {link.name}
                  </button>
                )
              ))}
            </div>
          </nav>

          {/* Theme Toggle & Spacer */}
          <div className="hidden lg:flex items-center relative z-10 gap-3">
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 ${textColor} relative z-[100] transition-opacity duration-300 ${
              isMobileMenuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open mobile navigation menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <Menu size={28} aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Apple-style Full-screen Mobile Menu Overlay — always mounted for instant toggle */}
      <div
        className={`lg:hidden fixed inset-0 z-[200] ${isMobileMenuOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!isMobileMenuOpen}
        {...(!isMobileMenuOpen ? { inert: '' as unknown as boolean } : {})}
        style={{ contain: 'strict' }}
      >
        {/* Blurred backdrop — GPU-accelerated opacity transition only */}
        <div
          className={`absolute inset-0 bg-muted/80 dark:bg-background/90 backdrop-blur-xl transition-opacity duration-150 ease-out ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onTouchStart={handleBackdropTouchStart}
          onTouchEnd={handleBackdropTouchEnd}
          onClick={() => {
            if (!('ontouchstart' in window)) {
              handleCloseMenu();
            }
          }}
          style={{ touchAction: 'none', willChange: 'opacity', transform: 'translate3d(0,0,0)' }}
        />

        {/* Full-screen menu panel — translateX + opacity (GPU) */}
        <div
          className={`absolute inset-0 flex flex-col h-[100dvh] transition-[opacity,transform] duration-150 ease-out ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            willChange: 'opacity, transform',
            transform: isMobileMenuOpen ? 'translate3d(0,0,0)' : 'translate3d(100%,0,0)',
          }}
        >


            {/* Fixed Header - Theme Toggle & Close button */}
            <div className="sticky top-0 z-[210] flex justify-between items-center px-6 pt-6 pb-4 bg-gradient-to-b from-muted/80 via-muted/60 to-transparent dark:from-background/90 dark:via-background/60">
              <ThemeToggle />
              <button
                className="w-12 h-12 flex items-center justify-center bg-background text-primary hover:bg-primary/5 rounded-2xl shadow-soft border border-border/30 transition-all duration-300 active:scale-95"
                onClick={handleCloseMenu}
                aria-label="Close mobile navigation menu"
              >
                <X size={24} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            
            {/* Scrollable content area */}
            <div id="mobile-menu" className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 pb-8 -webkit-overflow-scrolling-touch" role="navigation" aria-label="Mobile navigation" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Navigation Links — Liquid Glass card with airy spacing */}
              <nav className="mb-6" aria-label="Main navigation">
                <div className="glass-card rounded-3xl overflow-hidden p-2 sm:p-3">
                  {navLinks.map((link, index) => {
                    const [linkPath, linkHash] = link.href.split("#");
                    const isActive = link.hasMegaMenu
                      ? location.pathname.startsWith("/products")
                      : link.isRoute
                        ? location.pathname === link.href
                        : linkHash
                          ? location.pathname === (linkPath || "/") && location.hash === `#${linkHash}`
                          : false;

                    return link.hasMegaMenu ? (
                      <div key={link.name} className="mb-1 last:mb-0">
                        <button
                          onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setIsMobileProductsOpen(!isMobileProductsOpen);
                          }}
                          aria-expanded={isMobileProductsOpen}
                          aria-current={isActive ? "page" : undefined}
                          aria-label={`${link.name} submenu, ${isMobileProductsOpen ? 'expanded' : 'collapsed'}`}
                          className={`font-poppins relative w-full min-h-[48px] flex items-center justify-between pl-6 pr-5 py-4 rounded-2xl text-base sm:text-lg font-medium tracking-tight transition-all duration-150 ease-out touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 active:scale-[0.99] active:bg-primary/10 ${
                            isActive
                              ? "bg-primary/[0.06] dark:bg-white/[0.06] text-primary"
                              : "text-primary hover:bg-primary/[0.04] dark:hover:bg-white/[0.04]"
                          }`}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {isActive && (
                            <span aria-hidden="true" className="absolute left-1.5 top-3 bottom-3 w-[3px] rounded-full bg-secondary" />
                          )}
                          <span className={isActive ? "font-semibold" : ""}>{link.name}</span>
                          <ChevronDown
                            size={18}
                            strokeWidth={1.75}
                            aria-hidden="true"
                            className={`transition-transform duration-300 text-primary/40 ${isMobileProductsOpen ? 'rotate-180' : ''}`}
                          />
                        </button>

                        {/* Mobile Products Submenu */}
                        <div
                          className={`overflow-hidden transition-all duration-400 ease-out ${
                            isMobileProductsOpen ? 'max-h-[1000px] opacity-100 mt-1' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="py-1 px-1">
                            {products.map((product) => {
                              const isChildActive = location.pathname === `/products/${product.id}`;
                              return (
                                <Link
                                  key={product.id}
                                  to={`/products/${product.id}`}
                                  onClick={handleCloseMenu}
                                  aria-current={isChildActive ? "page" : undefined}
                                  className={`relative flex items-center gap-4 py-3 pl-5 pr-4 rounded-2xl mb-1 min-h-[48px] transition-all duration-150 ease-out touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 active:scale-[0.99] ${
                                    isChildActive
                                      ? "bg-secondary/[0.08] dark:bg-secondary/[0.12] text-primary"
                                      : "text-primary hover:bg-primary/[0.04] dark:hover:bg-white/[0.04]"
                                  }`}
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {isChildActive && (
                                    <span aria-hidden="true" className="absolute left-1 top-3 bottom-3 w-[3px] rounded-full bg-secondary" />
                                  )}
                                  <div className="w-10 h-10 rounded-xl bg-muted overflow-hidden flex-shrink-0">
                                    <img
                                      src={product.image}
                                      alt={product.shortName}
                                      width={40}
                                      height={40}
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <span className={`text-[0.95rem] ${isChildActive ? "font-semibold" : "font-medium"}`}>
                                    {product.shortName}
                                  </span>
                                </Link>
                              );
                            })}
                            <Link
                              to="/products"
                              onClick={handleCloseMenu}
                              className="block py-3 px-5 min-h-[44px] text-sm font-semibold text-secondary active:opacity-70 transition-opacity touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 rounded-xl"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              View All Products →
                            </Link>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        key={link.name}
                        onClick={() => handleNavClick(link)}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          handleNavClick(link);
                        }}
                        aria-current={isActive ? "page" : undefined}
                        aria-label={`Navigate to ${link.name} ${link.isRoute ? 'page' : 'section'}`}
                        className={`font-poppins relative w-full min-h-[48px] text-left pl-6 pr-5 py-4 mb-1 last:mb-0 rounded-2xl text-base sm:text-lg font-medium tracking-tight transition-all duration-150 ease-out touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 active:scale-[0.99] active:bg-primary/10 ${
                          isActive
                            ? "bg-primary/[0.06] dark:bg-white/[0.06] text-primary"
                            : "text-primary hover:bg-primary/[0.04] dark:hover:bg-white/[0.04]"
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {isActive && (
                          <span aria-hidden="true" className="absolute left-1.5 top-3 bottom-3 w-[3px] rounded-full bg-secondary" />
                        )}
                        <span className={isActive ? "font-semibold" : ""}>{link.name}</span>
                      </button>
                    );
                  })}
                </div>
              </nav>

            </div>
          </div>
        </div>


      {/* ZLD Calculator */}
      <ZLDCalculator isOpen={isCalculatorOpen} onOpenChange={setIsCalculatorOpen} />
    </>
  );
};

export default Header;